import { investorApi } from '@core/api/httpClient'
import { TRANSACTION_ENDPOINTS } from './transactionEndpointPaths'
import {
  parseTransactionsResponse,
  parseTransactionsFilters,
  type ParsedTransactionsPage,
  type TransactionsFiltersDto,
} from './transactionParser'
import type { TransactionFilters, DownloadFormat } from '../types/transactionTypes'

export interface FetchTransactionsParams {
  page: number
  limit: number
  filters: TransactionFilters
}

export async function getTransactions(
  params: FetchTransactionsParams,
): Promise<ParsedTransactionsPage> {
  const query: Record<string, string | number> = {
    page: params.page,
    limit: params.limit,
    sort: params.filters.sort,
  }

  if (params.filters.search) query.search = params.filters.search
  // The activity endpoint accepts `type` and `status` as comma-separated
  // lists. The current filter chip UI only sets one value at a time, so we
  // forward whatever single value (or already-comma-joined list) the panel
  // produced.
  if (params.filters.type) query.type = params.filters.type
  if (params.filters.status) query.status = params.filters.status
  if (params.filters.dateFrom) query.dateFrom = params.filters.dateFrom
  if (params.filters.dateTo) query.dateTo = params.filters.dateTo

  const { data } = await investorApi.get<unknown>(TRANSACTION_ENDPOINTS.LIST, { params: query })
  return parseTransactionsResponse(data)
}

export async function getTransactionsFilters(): Promise<TransactionsFiltersDto> {
  const { data } = await investorApi.get<unknown>(TRANSACTION_ENDPOINTS.FILTERS)
  return parseTransactionsFilters(data)
}

export async function downloadTransactions(
  format: DownloadFormat,
  filters: TransactionFilters,
): Promise<Blob> {
  // The activity download endpoint accepts `format` (csv | excel) plus an
  // optional date range. The list-page's search/type/status filters are not
  // re-applied to the download today — send only what the endpoint expects.
  const query: Record<string, string> = { format }
  if (filters.dateFrom) query.dateFrom = filters.dateFrom
  if (filters.dateTo) query.dateTo = filters.dateTo
  if (filters.dateFrom && filters.dateTo) query.dateRange = 'custom'

  const response = await investorApi.get(TRANSACTION_ENDPOINTS.DOWNLOAD, {
    params: query,
    responseType: 'blob',
  })
  return response.data as Blob
}
